import { BucketImage } from '../hooks/useBucketImage';
import { Address } from './types';
import { User } from './user';

export enum OrganisationType {
	EducationalInstitution = 'educationalInstitution',
	Company = 'company'
}

export interface Organisation {
	id: string;
	name: string;
	type: OrganisationType;
	currency?: string;

	logo?: BucketImage;
	banner?: BucketImage;
	bannerBackground?: string;
	email?: string;
	phone?: string;
	address?: Address;
	website?: string;
	slateID?: string;
	slateAPI?: string;
	slateUSERNAME?: string;
	slatePASSWORD?: string;
	grants?: {
		agents?: boolean;
		hr?: boolean;
		slate?: boolean;
	};
	verifierOnlyOrg?: boolean;
	license?: string;
	pearsanaRan?: boolean;
	extendCredit?: boolean;
	extendCreditDays?: number;
	firstTimeSetup?: boolean;
	aiChatbot?: boolean;
	showAdvertisement?: boolean;
	glassIntensity?: number;

	users: {
		organisationAdmins?: User[];
		registrars?: User[];
		admissionsOfficers?: User[];
		employees?: User[];
		students?: User[];
	};

	isPearsana?: boolean; // set on frontend
	slateAuthType?: string; // for slate
	slateApiKey?: string; // for slate
}

export interface OrganisationProfile {
	schoolType?: string;
	educationType?: string;
	id?: string;
	country?: string;
	state?: string;
	city?: string;
	fullAddress?: string;
	geometry?: object;
	transfer?: boolean;
	accomodation?: boolean;
	financialAid?: boolean;
	enrollment?: string;
	campus?: string;
	academicPrograms?: string[];
	coCurricularPrograms?: string[];
	additionalInformation?: string;
	type?: string;
	name?: string;
	organisationLogo?: BucketImage;
}

export interface OrganisationPreview {
	id: string;
	name: string;
	type: OrganisationType;
	license?: string;
}

export interface CreateOrganisationPayload {
	name: string;
	organisationEmail?: string;
	organisationAdminEmail?: string;
	subscriptionExpire?: string;
	products?: string[];
	license?: string;
	contactName?: string;
	referralFrom?: string;
	currency?: string;
	pearsanaRan?: boolean;
	extendCredit?: boolean;
	extendCreditDays?: number;
	type: OrganisationType;
	organisationAdmin: {
		email: string;
	};
	verifierOnlyOrg: boolean;
	grants?: {
		agents?: boolean;
		hr?: boolean;
		slate?: boolean;
	};
}

export interface UpdateOrganisationPayload {
	id: string;
	name: string;
	type: OrganisationType;
	logo?: BucketImage;
	banner?: BucketImage;
	bannerBackground?: string;
	email?: string;
	phone?: string;
	address?: Address;
	website?: string;
	aiChatbot?: boolean;
	showAdvertisement?: boolean;
	glassIntensity?: number;
	grants?: {
		agents?: boolean;
		hr?: boolean;
		slate?: boolean;
	};
}

export interface SlateUserInfo {
	slateID: string;
	slateAPI: string;
	username: string;
	password: string;
}

export interface OrgAdvertisement {
	organisationId: string;
	advertId?: string;
	profileId?: string;
	formData: {
		name: string;
		orgLogo: BucketImage;
		programType: string;
		programs: string[];
		schoolType: string;
		programName: string;
		programDescription: string;
	};
}

export interface ReadOrgAdvert {
	id: string;
	courseTitle: string;
	programType: string;
	duration: string;
	programName: string;
	positionTitle?: string;
}

export interface ReadSingleOrgAdvert {
	id: string;
	courseTitle: string;
	programType: string;
	acceptTransferRequests: boolean;
	acceptCurrentStudents: boolean;
	duration: string;
	entryRequirements: string;
}

export interface StudentList {
	id?: string;
	advertId?: string;
	profilePicture?: string;
	name: string;
	course: string;
	currentStudent: boolean;
	transferRequest: boolean;
}

// ── Organisation Constituents ───────────────────────────────────────────────

/**
 * A row on an organisation's constituent roster. `linked` is a boolean by design —
 * the API never exposes the platform user id behind a linked constituent.
 */
export interface OrganisationConstituent {
	constituentId: string;
	organisationId: string;
	email: string;
	name?: string;
	externalConstituentId?: string | null;
	attributes?: Record<string, string>;
	status: 'pending' | 'linked' | string;
	linked: boolean;
	linkedDateTime?: string | null;
	uploadedDateTime?: string | null;
	/** Where the row came from, so an admin can answer "why is this person here?". */
	source?: 'partnerApi' | 'csvImport' | 'unknown';
	/**
	 * Which API call produced it, named as the API names the route. The channel alone
	 * leaves an admin reading "Partner API" with no idea what the partner did; this
	 * says it was an account being provisioned. Null when the channel is the whole
	 * story, as it is for a CSV import.
	 */
	sourceOperation?: string | null;
	/** The partner's request id, or the import batch id. Neither identifies a person. */
	sourceReference?: string | null;
}

/**
 * An organisation's partner API configuration: the globally unique namespace it
 * addresses constituents under, and the licence nominated for them at onboarding.
 */
export interface PartnerConfig {
	organisationId: string;
	namespace: string;
	licenseProductId?: string | null;
	creationDateTime?: string | null;
	updatedDateTime?: string | null;
	updatedBy?: string | null;
}

/** A row rejected during upload, reported alongside the rows that did commit. */
export interface ConstituentUploadError {
	row: number;
	email?: string | null;
	code: string;
	message: string;
}

/** A parsed CSV row submitted for ingest. Sanitisation happens server-side. */
export interface ConstituentUploadRow {
	email: string;
	name: string;
	externalConstituentId?: string;
	attributes?: Record<string, string>;
}

export interface ConstituentUploadResult {
	uploadBatchId: string;
	summary: { added: number; updated: number; linked: number; skipped: number };
	constituents: OrganisationConstituent[];
	errors: ConstituentUploadError[];
}

/**
 * Lifecycle of a bulk import.
 *
 * `awaitingUpload` exists because the job record and the file are created by
 * separate calls: the plan is only queued once the upload is known to have landed,
 * so a worker can never reach for a file the browser is still sending.
 */
export type ConstituentImportStatus =
	| 'awaitingUpload'
	| 'queued'
	| 'planning'
	| 'processing'
	/** Purge only: chunks are done, the roster is being confirmed empty. */
	| 'verifying'
	| 'completed'
	| 'failed';

/** What POST .../constituents/imports returns: somewhere to put the file. */
export interface ConstituentImportTicket {
	jobId: string;
	uploadUrl: string;
	expiresIn: number;
	maxBytes: number;
}

/** Progress, as the dashboard polls it. Never carries the S3 key or a user id. */
export interface ConstituentImportJob {
	jobId: string;
	/** Which organisation this job belongs to — the client holds only one at a time. */
	organisationId: string;
	/**
	 * Import and purge share this record, the organisation lock and the progress
	 * counters — erasing a roster has the same GSI partition ceiling as writing one,
	 * so neither can run inside a request.
	 */
	kind: 'import' | 'purge';
	status: ConstituentImportStatus;
	fileName: string | null;
	totalRows: number;
	processedRows: number;
	percentComplete: number;
	summary: { added: number; updated: number; linked: number; skipped: number };
	errorCount: number;
	error: string | null;
	requestedBy: string | null;
	createdAt: string;
	finishedAt: string | null;
}

/** Statuses for which polling should continue. */
export const CONSTITUENT_IMPORT_ACTIVE_STATUSES: ConstituentImportStatus[] = [
	'awaitingUpload',
	'queued',
	'planning',
	'processing',
	'verifying'
];

// ── Partner API ─────────────────────────────────────────────────────────────

export interface AppClient {
	clientId: string;
	displayName: string;
	description?: string;
	allowedScopes?: string[];
	status?: string;
}

/** Returned by `createAppClient` — includes the one-time secret. */
export interface CreateAppClientResponse extends AppClient {
	clientSecret: string;
}

export interface PartnerApiLog {
	timestamp: string | null;
	level: string;
	message?: string | null;
	errorMessage?: string;
	route?: string | null;
	statusCode?: string;
	errorStatusCode?: string;
	requestId?: string | null;
	method?: string;
	path?: string;
	clientId?: string;
	resolvedVersion?: string;
	'error.code'?: string;
	errorName?: string;
	errorDetails?: string;
}

export interface FetchPartnerApiLogsParams {
	organisationId: string;
	hours?: number;
	status?: string;
	limit?: number;
	nextToken?: string | null;
	startTime?: number;
	endTime?: number;
	/** When true the reducer appends logs to existing state instead of replacing. */
	append?: boolean;
}
